// This object will be used when application does not have access to window.meristemContext
// One scenario for this to happen would be local development/debugging/testing
import { getRoute } from "@utils/meristemContext/productionRoutes";
import { EU_COUNTRIES } from "@utils/meristemContext/countries";
import { UAParser } from "ua-parser-js";

const devDefault = {
  device_brand: "Apple",
  device_family: "Mac",
  device_is_mobile: false,
  device_os: "Mac OS X",
  city_name: "New York",
  continent_code: "NA",
  country_code: "US",
  is_eu_citizen: false,
  postal_code: "10001",
  subdivision: "NY",
  is_forced_country_code: false,
  language_code: "en",
  route_id: "",
};

const prodDefault = {
  device_brand: "",
  device_family: "",
  device_is_mobile: false,
  device_os: "",
  city_name: "",
  continent_code: "NA",
  country_code: "US",
  is_eu_citizen: false,
  postal_code: "",
  subdivision: "",
  is_forced_country_code: false,
  language_code: "en",
  route_id: "_direct",
};

/**
 * This method replicates the forcing params system from the buyflow-worker, in order to work similarly on the local environment
 * @param searchParams
 */
function getForcingParameters(searchParams) {
  let countryCode = searchParams.get("fcc");
  const continentCode = searchParams.get("fcoc");
  const cityName = searchParams.get("fcn");
  const subdivision = searchParams.get("fsub");
  const postalCode = searchParams.get("fpc");

  countryCode = searchParams.get("cc") || countryCode;

  return {
    ...(countryCode && { country_code: countryCode }),
    ...(continentCode && { continent_code: continentCode }),
    ...(cityName && { city_name: cityName }),
    ...(subdivision && { subdivision }),
    ...(postalCode && { postal_code: postalCode }),
    is_forced_country_code: !!searchParams.get("fcc"),
    is_eu_citizen: countryCode && EU_COUNTRIES.includes(countryCode),
  };
}

/**
 * This method replicates the device information extraction from the buyflow-worker, in order to work similarly on the local environment
 */
function getDeviceParameters() {
  const userAgent = window?.navigator?.userAgent;
  const parsedUserAgent = new UAParser(userAgent);
  const device = parsedUserAgent.getDevice();
  const os = parsedUserAgent.getOS();

  return {
    device_is_mobile: Boolean(device?.type),
    ...(device.vendor && { device_brand: device.vendor }),
    ...(device.type && { device_family: device.type }),
    ...(os.name && { device_os: os.name }),
  };
}

/**
 * This is used mainly for development environment. It is falling back to "prodDefault" in case is not development env for safety,
 * but that should NEVER be the case, because on staging/production this method will never be called since window.meristemContext will be used instead
 */
export default function getMeristemDefaultValue() {
  if (process.env.NODE_ENV === "development") {
    const url = new URL(window.location);
    const route = getRoute(url);

    devDefault.route_id = route?.context?.route_id || "_direct";
    devDefault.language_code =
      url.searchParams?.get("lc") || route?.context?.language_code || "en";

    return Object.assign(
      {},
      devDefault,
      getForcingParameters(url.searchParams),
      getDeviceParameters()
    );
  }
  return prodDefault;
}
